import { revealItemInDir } from "@tauri-apps/plugin-opener";
import { isEmpty } from "es-toolkit/compat";
import { type FC, useCallback, useState } from "react";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { FolderIcon, SettingsIcon } from "@moment/icons";
import {
	env,
	isAgentInstructionPage,
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	usePathPageFile,
} from "@moment/state";
import { BasicTooltip, Button, IconButton, TooltipContent, TooltipTrigger } from "@moment/ui";

import { Row } from "~/components/canvas/layout/row/Row";
import { PagePropertiesDialog } from "~/components/canvas/navigation-pane/PagePropertiesDialog";

import { PageTitle } from "./PageTitle";

type EditorHeaderProps = {
	writable: boolean;
	pagesMetadata?: DocumentMetadata;
	pageId?: string;
};

const RevealOnFilesystemButton: FC<{
	documentId: string;
	pageId: string;
	pagePath?: string;
	hasLocal: boolean;
}> = ({ documentId, pageId, pagePath, hasLocal }) => {
	const { path: pageFilePath } = usePathPageFile(documentId, pageId, pagePath);

	const handleRevealOnFilesystem = useCallback(async () => {
		if (!pageFilePath) return;
		await revealItemInDir(pageFilePath);
	}, [pageFilePath]);

	if (!pageFilePath || !hasLocal) return null;

	return (
		<>
			<div className="h-4 border-l border-primary" />
			<BasicTooltip>
				<TooltipTrigger asChild>
					<IconButton
						label="Reveal page on filesystem"
						preset="quaternary"
						size="sm"
						onClick={handleRevealOnFilesystem}
						icon={<FolderIcon className="opacity-50" />}
						borderRadius="default"
						className="text-secondary"
					/>
				</TooltipTrigger>
				<TooltipContent>Reveal page on filesystem</TooltipContent>
			</BasicTooltip>
		</>
	);
};

/**
 * <EditorHeader/>
 * Displays the header of a canvas—title, author, date published, and so on.
 */
export const EditorHeader: FC<EditorHeaderProps> = ({ writable, pagesMetadata, pageId }) => {
	// Hooks
	const { pageMetadata } = usePageFromCurrentRoute();
	const { documentId, documentListItem } = useDocumentFromCurrentRoute();
	const [optionsOpen, setOptionsOpen] = useState(false);

	const currentPage =
		pageMetadata ?? pagesMetadata?.pagesMetadata.find((page) => page.id === pageId);

	const isAgentPage = isAgentInstructionPage(currentPage?.path);

	return (
		currentPage && (
			<div className="group/header rich-text mt-4 @lg/main:mt-6 @xl/main:mt-6 flex flex-col">
				<Row>
					{writable && !isAgentPage && (
						<div className="flex items-center gap-1 opacity-0 group-hover/header:opacity-100 transition-opacity mb-1 mt-0.5 -ml-2.5">
							<Button
								preset="quaternary"
								size="sm"
								onClick={() => setOptionsOpen(true)}
								startIcon={<SettingsIcon className="opacity-50" />}
								className="text-secondary"
							>
								Options
							</Button>
							{(env.isDesktopApp || env.isMobileApp) &&
								documentId &&
								currentPage.id && (
									<RevealOnFilesystemButton
										documentId={documentId}
										pageId={currentPage.id}
										pagePath={currentPage.path}
										hasLocal={!isEmpty(documentListItem?.local)}
									/>
								)}
						</div>
					)}
					<PageTitle page={currentPage} writable={writable && !isAgentPage} />
				</Row>
				{writable && !isAgentPage && documentId && (
					<PagePropertiesDialog
						pageId={currentPage.id}
						documentId={documentId}
						open={optionsOpen}
						onOpenChange={setOptionsOpen}
					/>
				)}
			</div>
		)
	);
};
